// The starter checklist: five first steps shown as a small card on a new
// trainer's hunt rail. Each finished step pays a little (coins and
// account-bound Rune Seals); the card can be skipped at any time and goes away
// once everything is claimed, or on its own past STARTER_CLOSE_LEVEL (finished
// steps are paid then, so nothing earned is lost). Only trainers made after it
// shipped get one.
//
// Rewards stay tiny next to the hunt: 5 × 150 = 750 coins is about 21 kills
// on the first hunt (sim/targets.ts: starterCoinShare4h).
import type { TrainerDTO } from './protocol.ts';

export interface StarterState { did: string[]; claimed: string[]; skipped?: boolean }

export interface StarterStep { id: string; text: string; hint: string }

export const STARTER_STEPS: StarterStep[] = [
  { id: 'hunt', text: 'Pick a hunt', hint: 'Choose a creature on the hunt card, or a hunt on the Map.' },
  { id: 'catch', text: 'Catch a creature', hint: 'Tap a beaten wild to throw a seal at it.' },
  // Was "Set your lead" until 2026-10-24: a new player's only other creature is their Lv 1 first catch, which as lead
  // fainted within a minute and drank all five starting potions. Buying seals is the loop they need next.
  { id: 'shop', text: 'Buy seals in the Shop', hint: 'Seals run out fast. The Shop sells them for coins.' },
  { id: 'daily', text: 'Claim the daily reward', hint: 'The gift button in the top bar, or Quests under More on a phone.' },
  { id: 'quest', text: 'Claim a quest', hint: 'Finished quests show a Claim button on the quest card.' },
];
export const STARTER_CATCHES = 1;
export const STARTER_REWARD = { coins: 150, item: 'rune-seal', qty: 5 } as const;
export const STARTER_TOTAL_COINS = STARTER_REWARD.coins * STARTER_STEPS.length;

/**
 * A brand-new trainer's first throw always holds (Cole 2026-10-20: the tutorial's manual throw should never break free;
 * it was 1 in 4 before). Only the first catch: after it, catches run at the plain odds (about 1 in 100 throws for a
 * common with a Rune Seal), as in pokeidle, which has no boost at all. It was the first 3 until 2026-10-15, which
 * handed a new player a handful of the same species at once.
 */
export const FIRST_CATCHES = 1;
export const FIRST_CATCH_CHANCE = 1;

/**
 * Beginner's luck, fading (Cole 2026-10-28: no cliff where catching suddenly slows). A Rune Seal holds a common 1 time
 * in 100, so the 20 starting seals brought about one creature and new players left with none. A seal at a species the
 * trainer hasn't caught holds at least BEGINNER_CATCH_CHANCE with two species in the Bestiary (the starter and the
 * first catch), and each further species multiplies that floor by BEGINNER_FALLOFF: about 1 in 11 at the 5th
 * species and the plain odds (1 in 100 for a common with a Rune Seal) by the 10th (Cole 2026-10-28). Repeats always
 * use the plain odds, so it never hands out a pile of one species.
 */
export const BEGINNER_CATCH_CHANCE = 1 / 3;
export const BEGINNER_FALLOFF = 0.64;

/** The least a seal at a not-yet-caught species holds for a trainer with `have` species in the Bestiary. */
export const beginnerFloor = (have: number) => BEGINNER_CATCH_CHANCE * Math.pow(BEGINNER_FALLOFF, Math.max(0, have - 2));

/** A throw's chance with the new-trainer floors: the first catch always holds, then the fading floor on new species. */
export function newTrainerChance(t: Pick<TrainerDTO, 'stats' | 'dex'>, species: string, chance: number): number {
  if (t.stats.catches < FIRST_CATCHES) return Math.max(chance, FIRST_CATCH_CHANCE);
  if (t.dex[species]?.caught > 0) return chance;
  let have = 0;
  for (const d of Object.values(t.dex)) if (d.caught > 0) have++;
  return Math.max(chance, beginnerFloor(have));
}

type T = Pick<TrainerDTO, 'stats' | 'daily'> & { starter?: StarterState };

export function starterDone(t: T, id: string): boolean {
  const s = t.starter;
  if (!s) return false;
  if (id === 'catch') return t.stats.catches >= STARTER_CATCHES;
  if (id === 'daily') return !!t.daily.last;
  return s.did.includes(id);
}

/** Steps finished but not yet paid. */
export function starterReady(t: T): string[] {
  const s = t.starter;
  if (!s || s.skipped) return [];
  return STARTER_STEPS.filter((x) => starterDone(t, x.id) && !s.claimed.includes(x.id)).map((x) => x.id);
}

/** True while the card should show. */
export function starterOpen(t: T): boolean {
  const s = t.starter;
  // Counted by today's steps: a save that claimed the retired 'lead' step still owes the Shop one.
  return !!s && !s.skipped && STARTER_STEPS.some((x) => !s.claimed.includes(x.id));
}

/** Past this trainer level the card has done its job: it closes on its own, paying any finished step first. */
export const STARTER_CLOSE_LEVEL = 25;
export const starterExpired = (t: T & { level: number }) => starterOpen(t) && t.level >= STARTER_CLOSE_LEVEL;

/** Record an action toward a step; returns true when it changed anything. */
export function starterMark(t: T, id: string): boolean {
  const s = t.starter;
  if (!s || s.skipped || s.did.includes(id)) return false;
  s.did.push(id);
  return true;
}

// ---- Choosing a starter ----
// A new trainer picks one of three first creatures: a young beast from each
// mythology, all Uncommon (same stat total, so no pick is a power gap) and in a
// tide > flame > grove > tide circle. The starter is account-bound, so it never
// reaches the market. Until the pick is made the trainer holds the first choice,
// so the hunt never waits on it; picking swaps the species and keeps its rolls.
export const STARTER_CHOICES = ['nykling', 'kitling', 'leafcub'] as const;
export const STARTER_DEFAULT: (typeof STARTER_CHOICES)[number] = STARTER_CHOICES[0];

export function isStarterChoice(id: string): boolean {
  return (STARTER_CHOICES as readonly string[]).includes(id);
}
